// FI-2182: passkey (webauthn) helpers shared by the login page and the
// profile page's passkeys section. pure functions first (they run in mocha
// with no browser), then thin wrappers over navigator.credentials.
//
// the api speaks base64url for every byte field (python-fido2's websafe
// encoding, no padding); the browser wants ArrayBuffers in and hands
// ArrayBuffers back, so the two shapes meet here and nowhere else.

// per-browser markers in localStorage. the login page and App.vue logout()
// both clear localStorage on the way out and put these back (see
// readPasskeyMarkers / restorePasskeyMarkers), so they outlive a session.
//
// set on a browser that has added or signed in with a passkey: the login
// page asks for one on arrival and offers its fallback link there, nowhere
// else.
export const PASSKEY_HINT_KEY = 'fi_passkey_hint'
// set when the api refused a passkey this browser offered (a credential
// removed from the account that the authenticator still holds). the prompt
// on arrival stays off until a passkey works again here, so a retired one is
// not asked for on every visit.
export const PASSKEY_REFUSED_KEY = 'fi_passkey_refused'
// a timestamp written on an explicit sign-out: no prompt on arrival in any
// tab until it passes. short, because a later visit is a fresh arrival.
export const PASSKEY_QUIET_UNTIL_KEY = 'fi_passkey_quiet_until'
export const QUIET_AFTER_SIGNOUT_MS = 2 * 60 * 1000
const CARRIED_KEYS = [PASSKEY_HINT_KEY, PASSKEY_REFUSED_KEY, PASSKEY_QUIET_UNTIL_KEY]

export function b64urlToBuf(s) {
  const str = String(s || '')
  const pad = '='.repeat((4 - str.length % 4) % 4)
  const bin = atob((str + pad).replace(/-/g, '+').replace(/_/g, '/'))
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out.buffer
}

export function bufToB64url(buf) {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf)
  let bin = ''
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

// the api's registration options with the byte fields decoded for
// navigator.credentials.create
export function creationOptionsFromJson(o) {
  const opts = o || {}
  const user = opts.user || {}
  return {
    ...opts,
    challenge: b64urlToBuf(opts.challenge),
    user: { ...user, id: b64urlToBuf(user.id) },
    excludeCredentials: (opts.excludeCredentials || []).map(c => ({ ...c, id: b64urlToBuf(c.id) })),
  }
}

// the api's authentication options for navigator.credentials.get
export function requestOptionsFromJson(o) {
  const opts = o || {}
  return {
    ...opts,
    challenge: b64urlToBuf(opts.challenge),
    allowCredentials: (opts.allowCredentials || []).map(c => ({ ...c, id: b64urlToBuf(c.id) })),
  }
}

// a PublicKeyCredential in the json shape the api verifies. a registration
// carries attestationObject (+ transports); an assertion carries
// authenticatorData, signature and the userHandle the api matches rows on.
export function credentialToJson(cred) {
  const r = cred.response || {}
  const out = {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment || null,
    response: { clientDataJSON: bufToB64url(r.clientDataJSON) },
  }
  if (r.attestationObject) {
    out.response.attestationObject = bufToB64url(r.attestationObject)
    out.response.transports = typeof r.getTransports === 'function' ? r.getTransports() : []
  } else {
    out.response.authenticatorData = bufToB64url(r.authenticatorData)
    out.response.signature = bufToB64url(r.signature)
    out.response.userHandle = r.userHandle ? bufToB64url(r.userHandle) : null
  }
  return out
}

const win = (w) => w || (typeof window !== 'undefined' ? window : null)

export function webauthnSupported(w) {
  const g = win(w)
  return !!(g && g.PublicKeyCredential && g.navigator && g.navigator.credentials)
}

// the build flag alone. the api has its own flag (every passkey route is a
// 404 without it); this one keeps a prod bundle from even asking until both
// are flipped. the profile page gates on this one: an admin removing a lost
// device's passkey is a plain delete and needs no authenticator.
export function passkeysFlagOn(env) {
  const e = env || process.env
  return e.VUE_APP_PASSKEYS_ENABLED === 'true'
}

// the build flag and the browser together: what a ceremony needs
export function passkeysEnabled(w, env) {
  return passkeysFlagOn(env) && webauthnSupported(w)
}

// can the login page run the autofill (conditional mediation) ceremony
export async function conditionalUiAvailable(w) {
  const g = win(w)
  if (!webauthnSupported(g)) return false
  const fn = g.PublicKeyCredential.isConditionalMediationAvailable
  if (typeof fn !== 'function') return false
  try {
    return !!(await fn.call(g.PublicKeyCredential))
  } catch (_) {
    return false
  }
}

// can the login page ask for a passkey on arrival. not on webkit (safari, and
// every browser on iphone and ipad): its sheet for a request with no click
// behind it arrives seconds late, lands on someone already typing, and
// cancelling it early tears down the email field's suggestions with it. there
// the email field's suggestions are the way in, which is apple's own design.
// blink (chrome, edge, opera) and gecko (firefox) raise the sheet at once.
export function promptOnArrivalSupported(userAgent) {
  const ua = String(userAgent == null ? (typeof navigator !== 'undefined' ? navigator.userAgent : '') : userAgent)
  const webkitOnly = /AppleWebKit\//.test(ua) && !/(Chrome|Chromium|Edg|OPR)\//.test(ua)
  return !webkitOnly
}

// staff preview: mirrors the api's PASSKEY_ALLOWED_ROLES. widen here when the
// api allow-list widens.
export function canManagePasskeys(user) {
  return !!(user && user.role === 'admin')
}

// what to tell the user when a ceremony or api call fails. null means say
// nothing: the user closed the prompt, or we cancelled it ourselves.
// action: 'add' | 'replace' | 'remove' | 'list' | 'signin'
export function passkeyErrorMessage(e, action) {
  const name = e && e.name
  const status = e && e.response && e.response.status
  if (name === 'AbortError') return null
  if (name === 'NotAllowedError') {
    // the browser prompt was dismissed or timed out
    if (action === 'add') return 'Passkey setup was cancelled. Nothing was added.'
    if (action === 'replace') return 'Passkey setup was cancelled. Nothing was changed.'
    return null
  }
  if (name === 'InvalidStateError') {
    // excludeCredentials at work: this authenticator already holds a passkey
    // for the account
    if (action === 'replace') return 'This device holds a different passkey for your account. Replace that one instead.'
    return 'This device already has a passkey for your account. Use Replace on it to set up a fresh one.'
  }
  if (action === 'signin' && status === 401) return 'That passkey did not work. Try again or sign in with your password.'
  if (status === 404) return 'Passkeys are not available right now.'
  if (status === 403) return 'Passkeys are not available for this account yet.'
  if (action === 'signin') return 'Passkey sign-in did not go through. Try again or sign in with your password.'
  if (action === 'remove') return 'That passkey could not be removed. Try again or contact support.'
  if (action === 'list') return 'Your passkeys could not be loaded right now.'
  return 'Something went wrong with that passkey. Try again or contact support.'
}

// the jwt payload, or null. self-contained rather than reaching into
// session_renewal.js: this module only needs the pk claim.
export function jwtClaims(jwt) {
  try {
    const parts = String(jwt || '').split('.')
    if (parts.length < 2) return null
    const bytes = new Uint8Array(b64urlToBuf(parts[1]))
    let text = ''
    if (typeof TextDecoder !== 'undefined') {
      text = new TextDecoder().decode(bytes)
    } else {
      for (let i = 0; i < bytes.length; i++) text += String.fromCharCode(bytes[i])
    }
    const claims = JSON.parse(text)
    return claims && typeof claims === 'object' && !Array.isArray(claims) ? claims : null
  } catch (_) {
    return null
  }
}

// the passkey the current session was started with (the api's pk claim), so
// the list can mark it and a removal can hand the session over cleanly
export function sessionPasskeyId(jwt) {
  const claims = jwtClaims(jwt)
  return (claims && claims.pk) || null
}

// a passkey worked here (a sign-in, an add, a replace): the hint goes on and
// any earlier refusal is forgotten
export function rememberPasskeyOnThisBrowser(storage) {
  try {
    storage.setItem(PASSKEY_HINT_KEY, '1')
    storage.removeItem(PASSKEY_REFUSED_KEY)
  } catch (_) { /* storage blocked */ }
}

export function hasPasskeyOnThisBrowser(storage) {
  try { return storage.getItem(PASSKEY_HINT_KEY) === '1' } catch (_) { return false }
}

export function notePasskeyRefused(storage) {
  try { storage.setItem(PASSKEY_REFUSED_KEY, '1') } catch (_) { /* storage blocked */ }
}

export function passkeyRefusedOnThisBrowser(storage) {
  try { return storage.getItem(PASSKEY_REFUSED_KEY) === '1' } catch (_) { return false }
}

export function noteSignOut(storage, nowMs) {
  try { storage.setItem(PASSKEY_QUIET_UNTIL_KEY, String((nowMs == null ? Date.now() : nowMs) + QUIET_AFTER_SIGNOUT_MS)) } catch (_) { /* storage blocked */ }
}

export function quietAfterSignOut(storage, nowMs) {
  try {
    const until = Number(storage.getItem(PASSKEY_QUIET_UNTIL_KEY))
    return until > (nowMs == null ? Date.now() : nowMs)
  } catch (_) {
    return false
  }
}

export function clearSignOutQuiet(storage) {
  try { storage.removeItem(PASSKEY_QUIET_UNTIL_KEY) } catch (_) { /* storage blocked */ }
}

// the markers to put back after a localStorage.clear()
export function readPasskeyMarkers(storage) {
  const out = {}
  for (let i = 0; i < CARRIED_KEYS.length; i++) {
    try {
      const value = storage.getItem(CARRIED_KEYS[i])
      if (value != null) out[CARRIED_KEYS[i]] = value
    } catch (_) { /* storage blocked */ }
  }
  return out
}

export function restorePasskeyMarkers(storage, markers) {
  const keys = Object.keys(markers || {})
  for (let i = 0; i < keys.length; i++) {
    try { storage.setItem(keys[i], markers[keys[i]]) } catch (_) { /* storage blocked */ }
  }
}

// thin wrappers: the api's options json in, the credential json out
export async function createPasskey(optionsJson, w) {
  const g = win(w)
  const cred = await g.navigator.credentials.create({ publicKey: creationOptionsFromJson(optionsJson) })
  return credentialToJson(cred)
}

export async function getPasskey(optionsJson, { mediation, signal } = {}, w) {
  const g = win(w)
  const request = { publicKey: requestOptionsFromJson(optionsJson) }
  if (mediation) request.mediation = mediation
  if (signal) request.signal = signal
  const cred = await g.navigator.credentials.get(request)
  return credentialToJson(cred)
}
